import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Textarea from '@/components/ui/Textarea';
import Button from '@/components/ui/Button';
import DateTimeField, { toLocalInput, presetNow, presetPlusDays, presetEndOfMonth } from '@/components/ui/DateTimeField';

const empty = {
  name: '',
  description: '',
  discountPct: '',
  bannerText: '',
  startsAt: '',
  endsAt: '',
  active: true,
};

const toIso = (local) => (local ? new Date(local).toISOString() : null);
// ISO → input no fuso LOCAL (toISOString deslocaria a hora exibida).
const toLocal = (iso) => (iso ? toLocalInput(iso) : '');

/** Form de campanha promocional (banners). */
export default function CampaignForm({ initial, onSubmit, onCancel }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    ...empty,
    ...initial,
    discountPct: initial?.discountPct ?? '',
    startsAt: toLocal(initial?.startsAt),
    endsAt: toLocal(initial?.endsAt),
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setErr(t('campaigns.errNameRequired'));
    if (!form.startsAt || !form.endsAt) return setErr(t('campaigns.errPeriodRequired'));
    setSaving(true);
    setErr(null);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || null,
        bannerText: form.bannerText || null,
        startsAt: toIso(form.startsAt),
        endsAt: toIso(form.endsAt),
        active: !!form.active,
      };
      if (form.discountPct !== '' && form.discountPct != null) {
        payload.discountPct = Number(form.discountPct);
      }
      await onSubmit(payload);
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <Input label={t('campaigns.fieldName')} value={form.name} onChange={set('name')} placeholder={t('campaigns.placeholderName')} />
      <Textarea label={t('campaigns.fieldDescription')} value={form.description} onChange={set('description')} rows={2} />
      <Input label={t('campaigns.fieldBannerText')} value={form.bannerText} onChange={set('bannerText')}
        placeholder={t('campaigns.placeholderBannerText')} />
      <Input label={t('campaigns.fieldDiscountPct')} type="number" min="0" max="100" step="0.01"
        value={form.discountPct} onChange={set('discountPct')} placeholder="15" style={{ maxWidth: 160 }} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
        <DateTimeField
          label={t('campaigns.fieldStartsAt')}
          value={form.startsAt}
          onChange={(v) => setForm((f) => ({ ...f, startsAt: v }))}
          defaultTime="00:00"
          presets={[
            { label: t('datetime.presetNow'), value: presetNow },
            { label: t('datetime.presetTomorrow'), value: presetPlusDays(1) },
          ]}
        />
        <DateTimeField
          label={t('campaigns.fieldEndsAt')}
          value={form.endsAt}
          onChange={(v) => setForm((f) => ({ ...f, endsAt: v }))}
          defaultTime="23:59"
          presets={[
            { label: t('datetime.presetPlus7d'), value: presetPlusDays(7, { endOfDay: true }) },
            { label: t('datetime.presetPlus30d'), value: presetPlusDays(30, { endOfDay: true }) },
            { label: t('datetime.presetEndOfMonth'), value: presetEndOfMonth },
          ]}
        />
      </div>
      <Select label={t('screens.status')} value={form.active ? '1' : '0'}
        onChange={(e) => setForm((f) => ({ ...f, active: e.target.value === '1' }))}
        options={[{ value: '1', label: t('campaigns.statusActive') }, { value: '0', label: t('campaigns.statusInactive') }]} />
      {err && <span style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</span>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
        <Button type="button" variant="ghost" onClick={onCancel}>{t('common.cancel')}</Button>
        <Button type="submit" loading={saving}>{t('common.save')}</Button>
      </div>
    </form>
  );
}
